// Shared config for "Pal Stars Palsino" — the casino-style slot machine that
// pays out free booster packs. Reel symbols are real Palworld OFFICIAL CARD
// GAME printings; the roll itself happens server-side (see palsino.server.ts).
import { z } from "zod";

/** Free spins per member per UTC day. */
export const PALSINO_DAILY_SPINS = 30;

/** Hard cap on a single payout — the jackpot line. */
export const PALSINO_MAX_WIN = 30;

/** Anti-abuse: total packs Palsino can pay one account in a single UTC day. */
export const PALSINO_DAILY_PACK_CAP = 30;

/** Anti-abuse: minimum gap between two spins from the same account. */
export const PALSINO_MIN_SPIN_INTERVAL_MS = 1500;

/** Anti-abuse: burst ceiling — spins allowed in any rolling 60-second window. */
export const PALSINO_MAX_SPINS_PER_MINUTE = 12;

/** Anti-abuse: burst ceiling — spins allowed in any rolling 60-minute window. */
export const PALSINO_MAX_SPINS_PER_HOUR = 40;


/** What artwork a reel symbol draws: an official card scan, or site artwork. */
export type PalsinoArtKind = "card" | "sphere" | "egg";

export interface PalsinoSymbol {
  id: string;
  label: string;
  /** Exact printed card code used for the reel artwork (card symbols). */
  code: string;
  rarity: string;
  /** Relative reel weight. */
  weight: number;
  /** Packs paid for three of a kind. */
  triple: number;
  /** Whether a matching pair of this symbol pays the 1-pack consolation. */
  pairPays: boolean;
  tint: string;
  /** Artwork source. Defaults to the official card scan for `code`. */
  kind?: PalsinoArtKind;
  /** Sphere tier index (sphere art) or egg slug (egg art). */
  art?: string;
}

export const PALSINO_SYMBOLS: PalsinoSymbol[] = [
  { id: "rooby", label: "Rooby", code: "EBP01-011", rarity: "C", weight: 34, triple: 1, pairPays: false, tint: "#f87171" },
  { id: "cattiva", label: "Cattiva", code: "ETD02-023", rarity: "TD", weight: 30, triple: 1, pairPays: false, tint: "#fbcfe8" },
  { id: "foxparks", label: "Foxparks", code: "EBP01-006", rarity: "U", weight: 26, triple: 1, pairPays: false, tint: "#fb923c" },
  { id: "bushi", label: "Bushi", code: "EBP01-004", rarity: "R", weight: 18, triple: 2, pairPays: true, tint: "#facc15" },
  { id: "digtoise", label: "Digtoise", code: "EBP01-050", rarity: "RR", weight: 13, triple: 3, pairPays: true, tint: "#22c55e" },
  { id: "suzaku", label: "Suzaku", code: "EBP01-002", rarity: "RR", weight: 11, triple: 3, pairPays: true, tint: "#4ade80" },
  { id: "relaxaurus", label: "Relaxaurus", code: "EBP01-026SP", rarity: "SP", weight: 8, triple: 4, pairPays: true, tint: "#38bdf8" },
  { id: "penking", label: "Penking", code: "EBP01-031SR", rarity: "SR", weight: 7, triple: 5, pairPays: true, tint: "#0ea5e9" },
  { id: "lyleen", label: "Lyleen", code: "EBP01-049SSP", rarity: "SSP", weight: 4, triple: 12, pairPays: true, tint: "#86efac" },
  { id: "chillet", label: "Chillet", code: "EBP01-025SSP", rarity: "SSP", weight: 3, triple: 20, pairPays: true, tint: "#60a5fa" },
  { id: "jormuntide", label: "Jormuntide", code: "EBP01-027OSR", rarity: "OSR", weight: 3, triple: 10, pairPays: true, tint: "#818cf8" },
  { id: "jormuntide-ignis", label: "Jormuntide Ignis", code: "EBP01-001OSR", rarity: "OSR", weight: 2, triple: 8, pairPays: true, tint: "#c084fc" },
  { id: "gold-cattiva-soul", label: "Gold Cattiva Soul", code: "ESOUL-002", rarity: "SSS", weight: 3, triple: PALSINO_MAX_WIN, pairPays: true, tint: "#fbbf24" },
];

/**
 * Pal Sphere reel — capture spheres only, drawn as official in-game sphere item
 * icons. No TCG card scans appear on this machine; the Legendary Sphere is the
 * jackpot line.
 */
export const PALSINO_SPHERE_SYMBOLS: PalsinoSymbol[] = [
  { id: "sph-pal", label: "Pal Sphere", code: "", rarity: "Tier 1", weight: 34, triple: 1, pairPays: false, tint: "#93c5fd", kind: "sphere", art: "1" },
  { id: "sph-mega", label: "Mega Sphere", code: "", rarity: "Tier 2", weight: 27, triple: 1, pairPays: false, tint: "#60a5fa", kind: "sphere", art: "2" },
  { id: "sph-giga", label: "Giga Sphere", code: "", rarity: "Tier 3", weight: 20, triple: 2, pairPays: true, tint: "#38bdf8", kind: "sphere", art: "3" },
  { id: "sph-hyper", label: "Hyper Sphere", code: "", rarity: "Tier 4", weight: 14, triple: 4, pairPays: true, tint: "#22d3ee", kind: "sphere", art: "4" },
  { id: "sph-ultra", label: "Ultra Sphere", code: "", rarity: "Tier 5", weight: 9, triple: 8, pairPays: true, tint: "#a78bfa", kind: "sphere", art: "5" },
  { id: "sph-legendary", label: "Legendary Sphere", code: "", rarity: "Tier 6", weight: 4, triple: PALSINO_MAX_WIN, pairPays: true, tint: "#fbbf24", kind: "sphere", art: "6" },
];

/**
 * Palworld egg reel — official in-game egg artwork per family only. No TCG card
 * scans appear on this machine; the Huge Dragon Egg is the jackpot line.
 */
export const PALSINO_EGG_SYMBOLS: PalsinoSymbol[] = [
  { id: "egg-common", label: "Common Egg", code: "", rarity: "Common", weight: 34, triple: 1, pairPays: false, tint: "#e9dfc7", kind: "egg", art: "common-egg" },
  { id: "egg-damp", label: "Damp Egg", code: "", rarity: "Common", weight: 27, triple: 1, pairPays: false, tint: "#7fd3ef", kind: "egg", art: "damp-egg" },
  { id: "egg-electric", label: "Electric Egg", code: "", rarity: "Common", weight: 20, triple: 2, pairPays: true, tint: "#ffe066", kind: "egg", art: "electric-egg" },
  { id: "egg-large-dark", label: "Large Dark Egg", code: "", rarity: "Uncommon", weight: 14, triple: 4, pairPays: true, tint: "#a98bd8", kind: "egg", art: "large-dark-egg" },
  { id: "egg-large-dragon", label: "Large Dragon Egg", code: "", rarity: "Uncommon", weight: 9, triple: 6, pairPays: true, tint: "#8f7bff", kind: "egg", art: "large-dragon-egg" },
  { id: "egg-huge-dark", label: "Huge Dark Egg", code: "", rarity: "Rare", weight: 4, triple: 12, pairPays: true, tint: "#c084fc", kind: "egg", art: "huge-dark-egg" },
  { id: "egg-huge-dragon", label: "Huge Dragon Egg", code: "", rarity: "Rare", weight: 3, triple: PALSINO_MAX_WIN, pairPays: true, tint: "#fbbf24", kind: "egg", art: "huge-dragon-egg" },
];

export type PalsinoMachineId = "cards" | "spheres" | "eggs";

export interface PalsinoMachine {
  id: PalsinoMachineId;
  name: string;
  blurb: string;
  symbols: PalsinoSymbol[];
}

/** The three reel sets a player can choose between on /palsino. */
export const PALSINO_MACHINES: PalsinoMachine[] = [
  {
    id: "cards",
    name: "TCG Card Reels",
    blurb: "Real Palworld OFFICIAL CARD GAME scans — jackpot is three Gold Cattiva Soul.",
    symbols: PALSINO_SYMBOLS,
  },
  {
    id: "spheres",
    name: "Pal Sphere Reels",
    blurb: "Capture spheres from Pal Sphere to Legendary — no card scans; jackpot is three Legendary Spheres.",
    symbols: PALSINO_SPHERE_SYMBOLS,
  },
  {
    id: "eggs",
    name: "Palworld Egg Reels",
    blurb: "Official egg artwork, Common to Huge Dragon — no card scans; jackpot is three Huge Dragon Eggs.",
    symbols: PALSINO_EGG_SYMBOLS,
  },
];

export const PALSINO_MACHINE_IDS = PALSINO_MACHINES.map((m) => m.id);

export function palsinoMachine(id: string | null | undefined): PalsinoMachine {
  return PALSINO_MACHINES.find((m) => m.id === id) ?? PALSINO_MACHINES[0];
}

/** Every symbol across every machine — reel ids are unique site-wide. */
export const PALSINO_ALL_SYMBOLS: PalsinoSymbol[] = PALSINO_MACHINES.flatMap((m) => m.symbols);

export const PALSINO_SYMBOL_BY_ID: Record<string, PalsinoSymbol> = Object.fromEntries(
  PALSINO_ALL_SYMBOLS.map((s) => [s.id, s]),
);

/** Total reel weight for one machine. */
export function palsinoTotalWeight(symbols: PalsinoSymbol[]): number {
  return symbols.reduce((n, s) => n + s.weight, 0);
}

/** Payout order for one machine: biggest triple first, ties broken by rarity. */
export function palsinoPayoutTable(symbols: PalsinoSymbol[]): PalsinoSymbol[] {
  return [...symbols].sort((a, b) => b.triple - a.triple || a.weight - b.weight);
}


/** Packs paid when two reels match a pair-paying (R or better) symbol. */
export const PALSINO_PAIR_PAYOUT = 1;

/** Total reel weight — reels roll independently with these shares. */
export const PALSINO_TOTAL_WEIGHT = PALSINO_SYMBOLS.reduce((n, s) => n + s.weight, 0);

/**
 * Canonical payout order: biggest triple first, ties broken by rarity (lower
 * reel weight = rarer = higher). Both the simulator UI and the guide article
 * read this array so the two can never drift apart.
 */
export const PALSINO_PAYOUT_TABLE: PalsinoSymbol[] = [...PALSINO_SYMBOLS].sort(
  (a, b) => b.triple - a.triple || a.weight - b.weight,
);

/** Payout tier label used by the payout-table tooltip. */
export function palsinoTier(sym: PalsinoSymbol): "Jackpot" | "Chase" | "Mid" | "Base" {
  if (sym.triple >= PALSINO_MAX_WIN) return "Jackpot";
  if (sym.triple >= 5) return "Chase";
  if (sym.triple >= 2) return "Mid";
  return "Base";
}

/** Odds of three of a kind for one symbol, as a "1 in N" figure. */
export function palsinoTripleOdds(sym: PalsinoSymbol, total: number = PALSINO_TOTAL_WEIGHT): number {
  const share = sym.weight / total;
  return Math.round(1 / (share * share * share));
}

/** Single source of truth for the payout sentence shown in guides and copy. */
export function palsinoPayoutSentence(): string {
  const parts = PALSINO_PAYOUT_TABLE.map(
    (s) => `${s.label} (${s.rarity}) ${s.triple} pack${s.triple === 1 ? "" : "s"}`,
  );
  return `Three of a kind pays: ${parts.join(", ")}.`;
}

/** The jackpot symbol — referenced by guide copy, FAQ and the AI assistant. */
export const PALSINO_JACKPOT_SYMBOL = PALSINO_PAYOUT_TABLE[0];


/** Payout for a finished spin — never above PALSINO_MAX_WIN. */
export function palsinoPayout(ids: string[]): { packs: number; jackpot: boolean; line: string } {
  const [a, b, c] = ids;
  if (a === b && b === c) {
    const sym = PALSINO_SYMBOL_BY_ID[a];
    const packs = Math.min(PALSINO_MAX_WIN, sym?.triple ?? 1);
    return { packs, jackpot: packs >= PALSINO_MAX_WIN, line: `Triple ${sym?.label ?? a}` };
  }
  const pair = a === b ? a : b === c ? b : a === c ? a : null;
  if (pair) {
    const sym = PALSINO_SYMBOL_BY_ID[pair];
    if (sym?.pairPays) {
      return { packs: PALSINO_PAIR_PAYOUT, jackpot: false, line: `Pair of ${sym.label}` };
    }
    return { packs: 0, jackpot: false, line: `Pair of ${sym?.label ?? pair} — no payout` };
  }
  return { packs: 0, jackpot: false, line: "No line" };
}

export const spinSchema = z.object({}).default({});

/**
 * Free trial spins a first-time visitor gets before creating an account. The
 * reels roll and packs can be won, but the winnings only bank once they sign
 * up — packs can't be opened or filed into a binder without an account.
 */
export const PALSINO_GUEST_SPINS = 10;
